import './polyfills';

import { configureFetchLanguageData } from '@common/lang/services';
import { mockOAuth, retrieveInitialState, setupOAuth } from '@common/login/login';
import configureFetchLegacyPermissions from '@common/permissions/api';
import * as Sentry from '@sentry/browser';
import { createRoot } from 'react-dom/client';

import configureFetchAccount from './common/login/api';
import { accessToken as accessTokenStorage } from './common/tokenHandling/accessToken';
import { env } from './env';
import main, { OAuthConfigSettings, trace } from './main';
import { isExpectedHttpError } from './setup/errorReporting';
import { history, store } from './setup/store';

if (env.shouldSendMetrics) {
    const options: Sentry.BrowserOptions = {
        maxValueLength: 10000,
        dsn: env.runtimeConfig.sentryToken,
        release: `performance-${import.meta.env.COMMIT}`,
        ignoreErrors: ['TypeError: Failed to fetch'],
        beforeSend(event, hint) {
            const exception = hint?.originalException;
            if (exception instanceof Error && exception.stack?.includes('mapsjs-core')) {
                return null;
            }
            if (isExpectedHttpError(exception)) {
                return null;
            }
            return event;
        },
    };
    if (env.isRunningOnProd) {
        options.environment = 'production';
    } else if (env.isRunningOnStaging) {
        options.environment = 'staging';
    }
    if (env.featureToggles.tracing) {
        // eslint-disable-next-line no-console
        console.log('[sentryInit]', {
            host: window.location.host,
            shouldSendMetrics: env.shouldSendMetrics,
            isRunningOnProd: env.isRunningOnProd,
            isRunningOnStaging: env.isRunningOnStaging,
            environment: options.environment,
            release: options.release,
        });
    }
    Sentry.init(options);
}

const oauthBehavior = (settings: OAuthConfigSettings) => {
    const isMocking = env.featureToggles.mockedToken;
    const promise =
        isMocking || (env.runtimeConfig.login && env.runtimeConfig.login.mockAuthorization)
            ? mockOAuth(settings)
            : setupOAuth(settings);

    return promise.then(() => {
        const { initialRoute } = retrieveInitialState();

        trace('initialRoute lookup', initialRoute);
        if (initialRoute) {
            trace(`history.replace("/${initialRoute}")`);
            history.replace(`/${initialRoute}`);
        }

        return Promise.resolve();
    });
};

trace('BOOTSTRAP_START', {
    href: window.location.href,
    pathname: window.location.pathname,
    redirectUri: env.runtimeConfig.login.redirectUri,
    silentRedirectUri: env.runtimeConfig.login.silentRedirectUri,
});

trace('STARTING_MAIN_APP');

main({
    accessTokenStorage,
    configureFetchLanguageData,
    configureFetchLegacyPermissions,
    configureFetchAccount,
    document,
    oauthBehavior,
    createRoot,
    store,
});
